import flatten from 'lodash/flatten';

export const parsePlugin = (node) => {
  switch (node.plugin) {
    case 'markdown-html':
      // this output is already sanitised by Storybook's markdown-html plugin
      return node.html;
    case 'native-color-picker':
      return node.color;
    case 'json-field':
      return JSON.parse(node.json_string);
    default:
      return null;
  }
};

/**
 * Parse props from values supplied by Storyblok,
 * i.e. prop_realPropName => realPropname
 */
export const parseProps = (root) => {
  const props = {};

  Object.keys(root).forEach((key) => {
    if (key.startsWith('props_')) {
      const propVal = root[key];
      const propContainsComponents =
        Array.isArray(propVal) && propVal.length > 0 && Boolean(propVal[0].component);
      const propIsUrl = typeof propVal === 'object' && propVal.url !== undefined;
      const propIsNumber = ['props_level', 'props_ratingPercent'].indexOf(key) > -1;

      if (propVal.plugin) {
        props[key.replace(/^props_/, '')] = parsePlugin(propVal);
      } else if (propContainsComponents) {
        props[key.replace(/^props_/, '')] = propVal.map((child) => parseTree(child)); // eslint-disable-line no-use-before-define
      } else if (propIsNumber) {
        props[key.replace(/^props_/, '')] = parseInt(propVal, 10);
      } else if (propIsUrl) {
        props[key.replace(/^props_/, '')] = propVal.url;
      } else if (key.indexOf('props_searchFilter') === 0) {
        props.searchFilter = {
          ...(props.searchFilter && { ...props.searchFilter }),
          [key.split('searchFilter_')[1]]: propVal,
        };
      } else {
        props[key.replace(/^props_/, '')] = propVal;
      }
    }
  });

  return props;
};

const parseTree = (root) => {
  const { _uid: key, children, component: componentName } = root;
  const props = parseProps(root);

  /**
   * If this node has either no children prop, a non-array children prop, or a
   * zero-length array children prop, there’s no need to recurse any deeper;
   * simply return the current level node, appropriately structured.
   */
  if (!children || !Array.isArray(children) || children.length === 0) {
    return {
      componentName,
      key,
      props,
      children: children || [],
    };
  }

  /**
   * Otherwise, we need to iterate over all child content
   */
  return {
    componentName,
    key,
    props,
    children: children.map((child) => parseTree(child)),
  };
};

/**
 * Walks through an object tree returned by the Storyblok API, converting the
 * content prop into a nested tree of components and props.
 *
 * @param {object} tree
 *
 * For an example of the input structure / expected output, see
 * app/areas/landingPages/factories.test.js
 */
export function parseComponentTree(tree) {
  /**
   * For the initial top-level tree, we need to iterate over each named
   * top-level section block (i.e. the sections defined in Storyblok for the
   * page in question), and parse each of its children. The top-level section
   * blocks are every key other than _uid and component, so we filter these out,
   * then map over all the remaining sections, parsing each as a top-level tree
   * recursively.
   */
  const isParseableTreeSection = (key) => {
    const ignoredKeys = ['_uid', 'component', 'metadata'];
    return !ignoredKeys.includes(key) && Array.isArray(tree[key]);
  };

  const parseTreeSections = (key) => tree[key].map((child) => parseTree(child));
  const result = Object.keys(tree)
    .filter(isParseableTreeSection)
    .map(parseTreeSections);

  return flatten(result);
}
